import { Column } from '@qtek/shared/models';
import { isNonNullable, isNullable } from '@qtek/shared/utils';
import {
  combineLatest,
  EMPTY,
  MonoTypeOperatorFunction,
  Observable,
  of,
} from 'rxjs';
import { distinctUntilChanged, startWith } from 'rxjs/operators';
import { ExtendDataTable, QtGroupBy, QtGroupByPath } from '../../models';
import { transformerFactory } from '../transform';
import { ExtendDataTableByMeta } from './group-entity-meta';
import {
  getValueAtPath,
  setValueAtPath,
  summarizeGroup,
} from './grouping-utils';

export function isExtendedDataTable<T>(data: any): data is T & ExtendDataTable {
  return '__groupBy' in data;
}

export function isGroupRowInDataTable<T>(
  data: any
): data is T & ExtendDataTable {
  return '__groupId' in data;
}

export function groupData<T>(
  data: T[],
  groupBy: QtGroupBy<T>,
  valueAccessor: (row: T, id: string, subId?: string, type?: string) => any,
  columns: Column[] = []
): T[] {
  const toGroupByString = (value: unknown): string => {
    if (isNullable(value)) {
      return 'null';
    }
    return String(value);
  };

  const columnId = groupBy.columnName;
  const predefinedGroups = (groupBy.predefinedGroups ?? []).map(
    (group, index) => ({
      key: `__predefined_group_${index}`,
      label: group.label,
      description: group.description,
      values: group.values,
      index,
    })
  );
  const hasPredefinedGroups = predefinedGroups.length > 0;
  const predefinedGroupsByKey = new Map(
    predefinedGroups.map(group => [group.key, group])
  );

  const toComparableValue = (value: unknown): string | number | null => {
    if (isNullable(value)) {
      return null;
    }

    if (typeof value === 'string' || typeof value === 'number') {
      return value;
    }

    return String(value);
  };

  const getPredefinedGroupKey = (rowValue: unknown): string | null => {
    const rowValues = Array.isArray(rowValue)
      ? rowValue
      : isNullable(rowValue)
        ? []
        : [rowValue];

    const comparableRowValues = rowValues
      .map(value => toComparableValue(value))
      .filter(isNonNullable);

    const matchedGroup = predefinedGroups.find(group =>
      group.values.some(value =>
        comparableRowValues.includes(toComparableValue(value))
      )
    );

    return matchedGroup?.key ?? null;
  };

  const reducedDataGroups = hasPredefinedGroups
    ? data.reduce<(string | null)[]>((acc, curr) => {
        const currentValue = valueAccessor(curr, columnId);
        const groupValue = getPredefinedGroupKey(currentValue);
        if (!acc.includes(groupValue)) {
          return [...acc, groupValue];
        }

        return acc;
      }, [])
    : data.reduce<unknown[]>((acc, curr) => {
        const currentValue = valueAccessor(curr, columnId);
        if (!acc.includes(currentValue)) {
          return [...acc, currentValue];
        }

        return acc;
      }, []);

  const sortedDataGroups = hasPredefinedGroups
    ? reducedDataGroups.sort((a, b) => {
        if (isNullable(a) && isNonNullable(b)) {
          return 1;
        } else if (isNullable(b) && isNonNullable(a)) {
          return -1;
        }

        const indexA = isNonNullable(a)
          ? (predefinedGroupsByKey.get(String(a))?.index ??
            Number.MAX_SAFE_INTEGER)
          : Number.MAX_SAFE_INTEGER;
        const indexB = isNonNullable(b)
          ? (predefinedGroupsByKey.get(String(b))?.index ??
            Number.MAX_SAFE_INTEGER)
          : Number.MAX_SAFE_INTEGER;

        return indexA - indexB;
      })
    : reducedDataGroups.sort((a, b) => {
        if (isNullable(a) && isNonNullable(b)) {
          return -1;
        } else if (isNullable(b) && isNonNullable(a)) {
          return 1;
        }

        return 0;
      });

  let groupedData: (T | (T & ExtendDataTableByMeta))[] = [];

  sortedDataGroups.forEach(dataGroup => {
    const inGroupElements: T[] = [];

    data.forEach(element => {
      if (isNullable(columnId)) {
        return;
      }

      const currentRow = valueAccessor(element, columnId);

      const matchedGroup = hasPredefinedGroups
        ? getPredefinedGroupKey(currentRow)
        : currentRow;

      if (
        (hasPredefinedGroups || !Array.isArray(currentRow)) &&
        matchedGroup === dataGroup
      ) {
        inGroupElements.push(element);
      }
    });

    const groupSummarized = summarizeGroup(
      inGroupElements,
      valueAccessor,
      columns
    ) as T;

    const firstItem = inGroupElements[0];
    const extractedData = {
      ...((firstItem as any)?.data?.sid && {
        data: {
          sid: (firstItem as any).data.sid,
          data: { ...((groupSummarized as any)?.data?.data || {}) },
        },
      }),
      ...((firstItem as any)?.namespace && {
        namespace: (firstItem as any)?.namespace,
      }),
    };
    const populatedData: any = {};
    const predefinedGroupMeta =
      hasPredefinedGroups && isNonNullable(dataGroup)
        ? predefinedGroupsByKey.get(String(dataGroup))
        : null;
    const groupDisplayValue = hasPredefinedGroups
      ? (predefinedGroupMeta?.description ??
        predefinedGroupMeta?.label ??
        'null')
      : dataGroup;
    const groupByString = toGroupByString(groupDisplayValue);

    // Populate data with groupPropIdPaths like ['usr.acn']
    if (groupBy.groupPropIdPaths) {
      groupBy.groupPropIdPaths.forEach((propIdPath: string) => {
        const value = getValueAtPath(firstItem, propIdPath);
        if (value !== undefined) {
          setValueAtPath(populatedData, propIdPath, value);
        }
      });
    }

    const group: T & ExtendDataTableByMeta = {
      __groupBy: groupByString,
      __groupById: columnId,
      __totalGroupCount: inGroupElements.length,
      __groupId: `groupId_${isNullable(dataGroup) ? 'ungrouped' : String(dataGroup)}`,
      __predefinedGroup: hasPredefinedGroups
        ? {
            __groupByLabel: predefinedGroupMeta?.label,
            __groupByDescription: predefinedGroupMeta?.description,
          }
        : null,
      ...extractedData,
      ...populatedData,
      ...groupSummarized,
    };

    const groupElements: T[] = inGroupElements.map(element => {
      return { ...element, __groupBy: groupByString } as T;
    });

    groupedData = [...groupedData, group, ...groupElements];
  });

  return groupedData as (T | (T & ExtendDataTableByMeta))[];
}

export function qGroupBy<T>(
  groupBy: QtGroupBy<T>,
  valueAccessor: (row: T, id: string, subId?: string, type?: string) => any,
  columns: Column[] | Observable<Column[]> = []
): MonoTypeOperatorFunction<T[]> {
  if (!groupBy) {
    return transformerFactory<T, Column[]>(EMPTY, ([data]) => data);
  }

  const columns$: Observable<Column[]> = Array.isArray(columns)
    ? of(columns)
    : columns;

  const columnName$ = groupBy.columnName$.pipe(distinctUntilChanged());

  const changes$ = combineLatest([
    columnName$.pipe(startWith(null)),
    columns$.pipe(startWith([])),
  ]);

  return transformerFactory<T, [QtGroupByPath<T> | null, Column[]] | null>(
    changes$,
    ([data, changesValue]) => {
      if (!changesValue || !Array.isArray(changesValue)) {
        return data;
      }
      const columnsValue = changesValue[1] ?? [];
      return groupData<T>(data, groupBy, valueAccessor, columnsValue);
    }
  );
}
